import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { BlagomailMark } from '@/components/BlagomailMark';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/client';

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuth((s) => s.login);
  const loading = useAuth((s) => s.loading);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Errore durante il login');
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
        <div className="flex items-center gap-3">
          <BlagomailMark size={38} tone="white" />
          <span className="text-2xl font-bold tracking-tight">Blagomail</span>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          <h1 className="text-5xl font-bold leading-tight tracking-tight">
            La tua email,<br />potenziata dall&apos;AI.
          </h1>
          <p className="text-lg opacity-90 max-w-md">
            Triage automatico, risposte intelligenti e ricerca semantica. Powered by Anthropic Claude.
          </p>
        </motion.div>
        <p className="text-sm opacity-70">© 2026 Blagoweb S.r.l.</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-none">
          <CardHeader>
            <CardTitle>Accedi</CardTitle>
            <CardDescription>Bentornato. Inserisci le tue credenziali per continuare.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="tu@azienda.it" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{error}</div>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Accedi
              </Button>
              <p className="text-sm text-center">
                <Link to="/forgot-password" className="text-muted-foreground hover:text-primary hover:underline">
                  Password dimenticata?
                </Link>
              </p>
              <p className="text-sm text-muted-foreground text-center">
                Non hai un account?{' '}
                <Link to="/register" className="text-primary hover:underline font-medium">
                  Registrati
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
